// ── usePolymarketRelayPositions ────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/positions. Returns
// the trader's derived open positions (netCount, avgEntry, runningRealized)
// computed server-side from the persisted fills ledger.
//
// Pairs with usePolymarketRelayPnl for the open notional / realized
// summary line. Same unavailable-state surfacing pattern as the other
// poly relay hooks so consumers can render one shared error UI.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getPolyPnlViaRelay,
  listPolyPositionsViaRelay,
  type PolyRelayPnl,
  type PolyRelayPosition,
  type PolyRelayUnavailableCode,
} from '@/lib/polymarket/relay';

export type PolyRelayPositionsStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable';

export interface PolyRelayPositionsState {
  positions: PolyRelayPosition[];
  pnl: PolyRelayPnl | null;
  status: PolyRelayPositionsStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  lastFetchMs: number | null;
}

export interface UsePolymarketRelayPositionsOptions {
  enabled?: boolean;
  pollMs?: number;
  /** When true, also fetches /pnl on each tick. Defaults to true; pass
   *  false for views that only need open positions (no realized/open
   *  notional summary). */
  includePnl?: boolean;
}

export interface UsePolymarketRelayPositionsReturn extends PolyRelayPositionsState {
  refresh: () => Promise<void>;
}

const DEFAULT_POLL_MS = 10_000;

export function usePolymarketRelayPositions(
  options: UsePolymarketRelayPositionsOptions = {},
): UsePolymarketRelayPositionsReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const includePnl = options.includePnl ?? true;

  const [positions, setPositions] = useState<PolyRelayPosition[]>([]);
  const [pnl, setPnl] = useState<PolyRelayPnl | null>(null);
  const [status, setStatus] = useState<PolyRelayPositionsStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [unavailableCode, setUnavailableCode] = useState<PolyRelayUnavailableCode | null>(null);
  const [lastFetchMs, setLastFetchMs] = useState<number | null>(null);

  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus((prev) => (prev === 'idle' ? 'loading' : prev));
    try {
      const [posOut, pnlOut] = await Promise.all([
        listPolyPositionsViaRelay(),
        includePnl ? getPolyPnlViaRelay() : Promise.resolve(null),
      ]);
      setLastFetchMs(Date.now());
      if (posOut.ok) {
        setPositions(posOut.positions);
        setError(null);
        setUnavailableCode(null);
        setStatus('ready');
        if (pnlOut && pnlOut.ok) setPnl(pnlOut.pnl);
        else if (pnlOut && !pnlOut.ok) {
          // Pnl is best-effort; positions ok → still render. Don't flip
          // the whole state to error just because pnl failed.
          setPnl(null);
        }
      } else if (posOut.code) {
        setPositions([]);
        setPnl(null);
        setError(posOut.error);
        setUnavailableCode(posOut.code);
        setStatus('unavailable');
      } else {
        setError(posOut.error);
        setUnavailableCode(null);
        setStatus('error');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [includePnl]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    const tick = () => {
      setStatus((current) => {
        if (!stopped && current !== 'unavailable') {
          void refresh();
        }
        return current;
      });
    };
    const intervalId = window.setInterval(tick, pollMs);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, pollMs, refresh]);

  return { positions, pnl, status, error, unavailableCode, lastFetchMs, refresh };
}
